/**
 * Zentrale Gestaltungsmaße für den Developer-Testbed (U4).
 * Überall verwenden — keine ad-hoc-Schriftgrößen daneben.
 */

/**
 * Schriftskala und Maße folgen der Kundenoberfläche: Fließtext 14 px (`text-sm`),
 * Nebentext 13 px, Kleintext 12 px (`text-xs`), Seitentitel 20 px wie in
 * `src/components/ui/page-header.tsx`. Der Testbed soll sich anfühlen wie das
 * Produkt, nicht wie ein eigenes Werkzeug mit größerer Schrift.
 */
export const design = {
  schrift: {
    fliesstext: 14,
    sekundaer: 13,
    klein: 12,
    ueberschrift1: 20,
    ueberschrift2: 16,
    ueberschrift3: 14,
  },
  zeile: {
    minHoehe: 48,
    innenabstand: 12,
  },
  abstand: {
    block: 12,
    abschnitt: 20,
    seite: 24,
  },
  radius: {
    karte: 10,
    bedienelement: 8,
  },
  /** Statusband (A6) — Höhe und Zustandsfarben, keine Ampel. */
  statusband: {
    hoehePx: 56,
    farbe: {
      bereit: "var(--status-neutral-text)",
      /** Laufend: klares Blau, getrennt von Reifegrad-Ampel. */
      laeuft: "#5b8def",
      wartet_auf_dich: "var(--context-violet-accent)",
      erfolgreich: "var(--status-success-text)",
      fehlgeschlagen: "var(--status-error-text)",
    },
  },
} as const;

/**
 * Ampelfarben Grün / Orange / Rot — dieselben Tokens wie die Urteil-Boxen
 * (Sehr gut / Akzeptabel / Sehr schlecht). Überall diese Klassen nutzen.
 */
export const ampelClass = {
  gruen: {
    dot: "bg-[var(--status-success-text)]",
    text: "text-[var(--status-success-text)]",
    banner:
      "border-[var(--status-success-border)] bg-[var(--status-success-surface)] text-[var(--status-success-text)]",
    bannerSelected:
      "border-[var(--status-success-text)] bg-[var(--status-success-surface)] text-[var(--status-success-text)]",
    circle:
      "border-transparent bg-[var(--status-success-text)] text-[var(--surface-app)]",
    bar: "bg-[var(--status-success-text)] border-[var(--status-success-text)]",
  },
  orange: {
    dot: "bg-[var(--status-warning-text)]",
    text: "text-[var(--status-warning-text)]",
    banner:
      "border-[var(--status-warning-border)] bg-[var(--status-warning-surface)] text-[var(--status-warning-text)]",
    bannerSelected:
      "border-[var(--status-warning-text)] bg-[var(--status-warning-surface)] text-[var(--status-warning-text)]",
    circle:
      "border-transparent bg-[var(--status-warning-text)] text-[var(--surface-app)]",
    bar: "bg-[var(--status-warning-text)] border-[var(--status-warning-text)]",
  },
  rot: {
    dot: "bg-[var(--status-error-text)]",
    text: "text-[var(--status-error-text)]",
    banner:
      "border-[var(--status-error-border)] bg-[var(--status-error-surface)] text-[var(--status-error-text)]",
    bannerSelected:
      "border-[var(--status-error-text)] bg-[var(--status-error-surface)] text-[var(--status-error-text)]",
    circle:
      "border-transparent bg-[var(--status-error-text)] text-[var(--surface-app)]",
    bar: "bg-[var(--status-error-text)] border-[var(--status-error-text)]",
  },
  grau: {
    dot: "border-2 border-[var(--status-neutral-text)] bg-transparent",
    text: "text-[var(--text-muted)]",
    banner:
      "border-[var(--border-default)] bg-[var(--surface-secondary)] text-[var(--text-secondary)]",
    bannerSelected:
      "border-[var(--border-strong)] bg-[var(--surface-secondary)] text-[var(--text-primary)]",
    circle:
      "border-2 border-[var(--status-neutral-text)] bg-transparent text-[var(--text-muted)]",
    bar: "bg-transparent border-[var(--status-neutral-text)]",
  },
} as const;

export type AmpelFarbeKey = keyof typeof ampelClass;

/** Urteilsskala → Ampelfarbe (nur Grün / Orange / Rot). */
export const urteilAmpelFarbe: Record<string, Exclude<AmpelFarbeKey, "grau">> = {
  sehr_gut: "gruen",
  gut: "gruen",
  akzeptabel: "orange",
  schlecht: "rot",
  sehr_schlecht: "rot",
};

/** Tailwind-Klassen aus den zentralen Maßen. */
export const designClass = {
  fliesstext: "text-[14px] leading-relaxed",
  sekundaer: "text-[13px] leading-snug",
  klein: "text-[12px] leading-snug",
  ueberschrift1: "text-[20px] font-semibold tracking-tight leading-tight",
  ueberschrift2: "text-[16px] font-semibold tracking-tight leading-snug",
  ueberschrift3: "text-[14px] font-medium leading-snug",
  /** Tabellen-/Listenzeile: mind. 48 px, Innenabstand 12. */
  zeile: "min-h-[48px] px-3 py-3",
  zeileCompact: "min-h-[44px] px-3 py-2",
  abschnitt: "space-y-5",
  blockGap: "gap-3",
  seite: "p-6",
  seiteX: "px-6",
  seiteY: "py-6",
  karte: "rounded-[10px]",
  /**
   * Inhaltsbox mit Kontrast zum Seitenhintergrund (wie sekundäre Flächen /
   * Seitenleiste — `surface-secondary`).
   */
  box: "rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-secondary)]",
  bedienelement: "rounded-[8px]",
  /** Nur Kennungen (WS04-E02) und Code. */
  kennung: "font-mono text-[12px] tracking-tight",
  code: "font-mono text-[12px]",
} as const;

/**
 * Seitenleiste des Testbeds — Maße, Farben und Symbolgrößen identisch zur
 * Hauptnavigation in `src/components/dashboard-shell.tsx`.
 */
export const sidebarClass = {
  zeile:
    "relative flex h-9 items-center gap-2.5 rounded-[var(--radius-control)] px-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] motion-reduce:transition-none",
  zeileAktiv: "bg-[var(--surface-selected)] text-[var(--text-primary)]",
  zeileRuhend:
    "text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-secondary)]",
  beschriftung: "truncate text-[13px] font-medium",
  symbol: "h-[18px] w-[18px] shrink-0",
} as const;
